import React, { useState, useEffect } from 'react';
import {
  getAppeals,
  generateDefenseFirst,
  generateDefenseSecond,
  saveDefenseEdit,
  downloadDefense,
  getDefenseText,
  getAutoPdfUrl,
  uploadRespostaJuiz
} from '../api';
import './ProcessDetail.css';

const STATUS_LABEL = {
  extraida:            'Extraída',
  em_tratamento:       'Em Tratamento',
  defesa_gerada:       'Defesa Gerada',
  aguardando_resposta: 'Aguardando Resposta',
  segunda_instancia:   'Segunda Instância',
  arquivada:           'Arquivada'
};

function ProcessDetail({ process: fine, onBack }) {
  const empresaId = localStorage.getItem('empresaId') || 'default';

  const [defenseText, setDefenseText]   = useState('');
  const [editMode, setEditMode]         = useState(false);
  const [editedText, setEditedText]     = useState('');
  const [instancia, setInstancia]       = useState(1);
  const [loading, setLoading]           = useState(false);
  const [loadingMsg, setLoadingMsg]     = useState('');
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const [respostaJuizFile, setRespostaJuizFile] = useState(null);
  const [defesa1Gerada, setDefesa1Gerada] = useState(false);
  const [readUrl, setReadUrl]           = useState(null);
  const [autoPdfUrl, setAutoPdfUrl]     = useState(null);
  const [autoPdfPage, setAutoPdfPage]   = useState(1);
  const [showPdf, setShowPdf]           = useState(false);

  const deadlineExpired = fine.deadlineExpired || (() => {
    if (!fine.deadline) return false;
    const [d, m, y] = fine.deadline.split('/');
    return new Date(y, m - 1, d) < new Date();
  })();

  useEffect(() => { loadRecurso(); }, []);

  const loadRecurso = async () => {
    try {
      const appeals = await getAppeals(empresaId);
      const r = appeals.find(a => a.auto === fine.auto);
      if (r) {
        const inst = r.instancia || 1;
        setInstancia(inst);
        if (r.s3_defesa_1) {
          setDefesa1Gerada(true);
          // Carregar texto completo da defesa
          await loadDefenseText(inst);
        }
      }
    } catch (e) { console.error('Erro ao carregar recurso:', e); }
  };

  const loadDefenseText = async (inst = instancia) => {
    try {
      const result = await downloadDefense(empresaId, fine.auto, String(inst));
      if (result.read_url) {
        setReadUrl(result.read_url);
        const text = await getDefenseText(result.read_url);
        setDefenseText(text);
      }
    } catch (e) {
      console.error('Erro ao carregar texto da defesa:', e);
    }
  };

  const handleGenerateDefense = async () => {
    setError(''); setLoading(true);

    if (instancia === 1) {
      setLoadingMsg('Gerando defesa de 1ª instância com IA...');
      try {
        await generateDefenseFirst(empresaId, fine.auto);
        setDefesa1Gerada(true);
        setSuccess('Defesa gerada! Carregando texto completo...');
        await loadDefenseText(1);
        setSuccess('Defesa gerada com sucesso! Revise e edite antes de fazer o download.');
      } catch (e) { setError(e.message); }

    } else {
      if (!respostaJuizFile) {
        setError('Anexe o arquivo de resposta do juiz antes de gerar a defesa de 2ª instância.');
        setLoading(false); return;
      }
      setLoadingMsg('Enviando resposta do juiz...');
      try {
        const respostaKey = await uploadRespostaJuiz(empresaId, fine.auto, respostaJuizFile);
        setLoadingMsg('Gerando defesa de 2ª instância com IA...');
        await generateDefenseSecond(empresaId, fine.auto, respostaKey);
        setSuccess('Defesa de 2ª instância gerada! Carregando...');
        await loadDefenseText(2);
        setSuccess('Defesa de 2ª instância gerada! Revise antes de fazer o download.');
      } catch (e) { setError(e.message); }
    }
    setLoading(false); setLoadingMsg('');
  };

  const handleSaveEdit = async () => {
    setError(''); setLoading(true); setLoadingMsg('Salvando alterações...');
    try {
      // Opção B: sobrescreve o arquivo original
      await saveDefenseEdit(empresaId, fine.auto, instancia, editedText);
      setDefenseText(editedText);
      setEditMode(false);
      setSuccess('Alterações salvas. Texto atualizado.');
      // Recarregar read_url atualizada
      await loadDefenseText(instancia);
    } catch (e) { setError(e.message); }
    setLoading(false); setLoadingMsg('');
  };

  const handleDownload = async () => {
    setError(''); setLoading(true); setLoadingMsg('Preparando download...');
    try {
      const { url, filename } = await downloadDefense(empresaId, fine.auto, String(instancia));
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      setSuccess('Download iniciado.');
    } catch (e) { setError(e.message); }
    setLoading(false); setLoadingMsg('');
  };

  const handleShowAutoPdf = async () => {
    if (autoPdfUrl) { setShowPdf(true); return; }
    setLoading(true); setLoadingMsg('Carregando auto de infração...');
    try {
      const result = await getAutoPdfUrl(empresaId, fine.auto);
      setAutoPdfUrl(result.url);
      setAutoPdfPage(result.page || 1);
      setShowPdf(true);
    } catch (e) { setError(e.message); }
    setLoading(false); setLoadingMsg('');
  };

  const hasDefense = !!defenseText;

  return (
    <div className="process-detail">
      <div className="detail-header">
        <button onClick={onBack} className="back-btn">← Voltar</button>
        <div className="header-info">
          <h1>Auto {fine.auto}</h1>
          <span className={`status-badge status-${(fine.status || 'extraida').replace('_', '-')}`}>
            {STATUS_LABEL[fine.status] || fine.status || 'Extraída'}
          </span>
          {deadlineExpired && (
            <span className="deadline-expired-badge">⚠️ Prazo Expirado</span>
          )}
          {fine.page && (
            <button className="btn-view-pdf" onClick={handleShowAutoPdf} disabled={loading}>
              📄 Ver Auto (pág. {fine.page})
            </button>
          )}
        </div>
      </div>

      {error   && <div className="alert alert-error"   onClick={() => setError('')}>⚠️ {error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess('')}>✅ {success}</div>}
      {loading && <div className="alert alert-loading"><span className="spinner" /> {loadingMsg || 'Aguarde...'}</div>}

      {/* Modal PDF do auto */}
      {showPdf && autoPdfUrl && (
        <div className="pdf-modal-overlay" onClick={() => setShowPdf(false)}>
          <div className="pdf-modal" onClick={e => e.stopPropagation()}>
            <div className="pdf-modal-header">
              <span>Auto {fine.auto} — Página {autoPdfPage}</span>
              <button onClick={() => setShowPdf(false)}>✕</button>
            </div>
            <iframe
              src={`${autoPdfUrl}#page=${autoPdfPage}`}
              title={`Auto ${fine.auto}`}
              width="100%"
              height="600px"
            />
          </div>
        </div>
      )}

      <div className="detail-grid">
        {/* Dados da Multa */}
        <div className="info-section">
          <div className="section-card">
            <h2>Dados da Multa</h2>
            <div className="info-grid">
              <InfoRow label="Notificação"    value={fine.notificacao} />
              <InfoRow label="Auto"           value={fine.auto} />
              <InfoRow label="Concessionária" value={fine.concessionaria} />
              <InfoRow label="Linha"          value={fine.linha} />
              <InfoRow label="Placa"          value={fine.placa} />
              <InfoRow label="Veículo"        value={fine.veiculo} />
              <InfoRow label="Data"           value={fine.data} />
              <InfoRow label="Hora"           value={fine.hora} />
              <InfoRow label="Local"          value={fine.local} />
              <InfoRow label="Código"         value={fine.codigo} />
              <InfoRow label="Valor"          value={fine.valor} />
              <InfoRow label="Prazo Recurso"  value={fine.deadline} highlight expired={deadlineExpired} />
            </div>
          </div>
          {fine.descricao && (
            <div className="section-card">
              <h2>Descrição da Infração</h2>
              <p className="descricao-text">{fine.descricao}</p>
            </div>
          )}
        </div>

        {/* Geração de Defesa */}
        <div className="defense-section">
          <div className="section-card">
            <h2>Instância</h2>

            {deadlineExpired && (
              <div className="deadline-warning">
                ⚠️ O prazo para recurso desta multa está expirado.<br />
                <span>A geração de defesa está bloqueada. O processo fica registrado para fins de histórico e relatórios.</span>
              </div>
            )}

            {instancia === 1 && defesa1Gerada && !deadlineExpired && (
              <div className="defense-already-generated">
                ✅ Defesa de 1ª instância já foi gerada para este auto.<br />
                <span>Use o editor abaixo para revisar e o botão Download para obter o documento final.</span>
              </div>
            )}

            <div className="instancia-toggle">
              {/* 1ª instância sempre ativo como padrão */}
              <button
                className={instancia === 1 ? 'active' : ''}
                onClick={() => { setInstancia(1); setEditMode(false); setError(''); if (defesa1Gerada) loadDefenseText(1); }}
                disabled={loading || deadlineExpired}>
                1ª Instância
              </button>
              <button
                className={instancia === 2 ? 'active' : ''}
                onClick={() => { setInstancia(2); setEditMode(false); setError(''); loadDefenseText(2); }}
                disabled={loading || deadlineExpired}>
                2ª Instância
              </button>
            </div>

            {instancia === 2 && !deadlineExpired && (
              <div className="resposta-juiz-upload">
                <label>Resposta do Juiz (obrigatório)</label>
                <input type="file" accept=".txt,.pdf,.doc,.docx"
                  onChange={e => setRespostaJuizFile(e.target.files[0])}
                  disabled={loading} />
                {respostaJuizFile && <span className="file-selected">📎 {respostaJuizFile.name}</span>}
              </div>
            )}

            <button className="btn-generate"
              onClick={handleGenerateDefense}
              disabled={loading || deadlineExpired || (instancia === 2 && !respostaJuizFile) || (instancia === 1 && defesa1Gerada)}
              title={deadlineExpired ? 'Prazo expirado' : (instancia === 1 && defesa1Gerada) ? 'Já gerada' : ''}>
              {deadlineExpired ? '🔒 Defesa Bloqueada — Prazo Expirado'
                : (instancia === 1 && defesa1Gerada) ? '🔒 Defesa de 1ª Instância Já Gerada'
                : loading ? '⏳ Gerando...'
                : `⚡ Gerar Defesa ${instancia}ª Instância`}
            </button>
          </div>

          {/* Editor — sempre visível se houver defesa */}
          {hasDefense && (
            <div className="section-card">
              <div className="defense-header">
                <h2>Defesa — {instancia}ª Instância</h2>
                <div className="defense-actions">
                  {!editMode ? (
                    <>
                      <button className="btn-edit"
                        onClick={() => { setEditedText(defenseText); setEditMode(true); }}
                        disabled={loading}>✏️ Editar</button>
                      <button className="btn-download"
                        onClick={handleDownload}
                        disabled={loading}>⬇️ Download</button>
                    </>
                  ) : (
                    <>
                      <button className="btn-save" onClick={handleSaveEdit} disabled={loading}>
                        💾 Salvar Alterações
                      </button>
                      <button className="btn-cancel"
                        onClick={() => { setEditMode(false); setEditedText(''); }}
                        disabled={loading}>Cancelar</button>
                    </>
                  )}
                </div>
              </div>

              {editMode ? (
                <div className="defense-editor">
                  <p className="editor-hint">✏️ Edite o texto abaixo. Ao salvar, o arquivo original será substituído.</p>
                  <textarea
                    value={editedText}
                    onChange={e => setEditedText(e.target.value)}
                    className="defense-textarea"
                    disabled={loading}
                    style={{ minHeight: '600px', height: 'auto' }}
                  />
                </div>
              ) : (
                <div className="defense-preview">
                  <pre>{defenseText}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, highlight, expired }) {
  return (
    <div className={`info-row ${highlight ? 'highlight' : ''} ${expired ? 'expired' : ''}`}>
      <span className="info-label">{label}</span>
      <span className="info-value" style={expired ? { color: '#f87171' } : {}}>
        {value || '—'} {expired && '⚠️'}
      </span>
    </div>
  );
}

export default ProcessDetail;